import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2, FileSpreadsheet, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ImportStepper from "@/components/import/ImportStepper";
import ImportDropzone from "@/components/import/ImportDropzone";
import ImportMapping from "@/components/import/ImportMapping";
import ImportDuplicates from "@/components/import/ImportDuplicates";
import ImportPreview from "@/components/import/ImportPreview";
import ImportSuccess from "@/components/import/ImportSuccess";
import { translateFr as tfr } from "@/i18n";

const STEPS = [
  { key: "upload", icon: "upload", label: "Fichier", sub: tfr("Télécharger") },
  { key: "mapping", icon: "map", label: "Mapping", sub: "Colonnes" },
  { key: "dup", icon: "dup", label: "Doublons", sub: tfr("Vérification") },
  { key: "preview", icon: "preview", label: tfr("Aperçu"), sub: tfr("Données") },
  { key: "done", icon: "done", label: "Importer", sub: "Finaliser" },
];

export default function ImportPage() {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [data, setData] = useState(null); // { headers, rows, mapping, duplicates, ai_used, total_rows }
  const [decisions, setDecisions] = useState({}); // duplicate decisions
  const [result, setResult] = useState(null);

  const sampleRow = useMemo(() => (data?.rows?.[0] || {}), [data]);
  const requiredOk = data && Object.values(data.mapping || {}).includes("name");

  const analyzeFile = async (f) => {
    setFile(f);
    setAnalyzing(true);
    setData(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData(r.data);
      setDecisions({});
      if (r.data.ai_used) toast.success(tfr("Mapping détecté automatiquement"));
      setStep(1);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Impossible de lire le fichier");
    } finally {
      setAnalyzing(false);
    }
  };

  const setMapping = (mapping) => setData((d) => ({ ...d, mapping }));

  const commit = async () => {
    if (!data) return;
    setCommitting(true);
    try {
      const r = await api.post("/import/commit", {
        rows: data.rows,
        mapping: data.mapping,
        skip_duplicates: true, // default fallback for unset duplicate rows
        default_status: "Repéré",
        duplicate_decisions: Object.fromEntries(
          Object.entries(decisions).map(([k, v]) => [String(k), v])
        ),
      });
      setResult(r.data);
      setStep(4);
      if (r.data?.limit_reached) {
        toast.warning(`${tfr("Limite de joueurs atteinte pour votre offre")}${r.data.player_limit ? ` (${r.data.player_limit})` : ""}. ${tfr("Certaines lignes n'ont pas été importées.")}`);
      }
    } catch {
      toast.error(tfr("Erreur lors de l'import"));
    } finally {
      setCommitting(false);
    }
  };

  const reset = () => {
    setStep(0);
    setFile(null);
    setData(null);
    setDecisions({});
    setResult(null);
  };

  const canNext = (() => {
    if (step === 0) return !!data;
    if (step === 1) return requiredOk;
    if (step === 2) return true;
    if (step === 3) return true;
    return false;
  })();

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto animate-fade-in pb-24 md:pb-8">
      {/* Page header */}
      <header className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground dark:text-muted-foreground/70">Import</div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mt-1">{tfr("Importer des joueurs")}</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          {tfr("CSV ou Excel. Le mapping des colonnes est suggéré automatiquement par l'IA.")}
        </p>
      </header>

      {/* Stepper */}
      <div className="rounded-3xl border border-border dark:border-white/10 bg-card dark:bg-surface p-5 md:p-6 mb-6" data-testid="import-stepper-card">
        <ImportStepper steps={STEPS} current={step} />
      </div>

      {/* Step content */}
      <div className="rounded-3xl border border-border dark:border-white/10 bg-card dark:bg-surface p-5 md:p-6 mb-6" data-testid="import-content-card">
        {/* File metadata banner (visible from step 1+) */}
        {step >= 1 && step <= 3 && file && data && (
          <div className="flex items-center gap-3 flex-wrap mb-5 pb-5 border-b border-border/70 dark:border-white/[0.05]">
            <Badge variant="outline" className="gap-1.5 border-border dark:border-white/10 bg-background/50">
              <FileSpreadsheet className="h-3.5 w-3.5" />{file.name}
            </Badge>
            <Badge variant="outline" className="border-border dark:border-white/10 bg-background/50">
              {data.total_rows} ligne{data.total_rows > 1 ? "s" : ""}
            </Badge>
            {data.ai_used && (
              <Badge className="bg-info/10 text-info border-info/30 gap-1.5">
                <Sparkles className="h-3 w-3" /> IA Claude
              </Badge>
            )}
            {data.duplicates?.length > 0 && (
              <Badge className="bg-warning/10 text-warning border-warning/30">
                {data.duplicates.length} doublon{data.duplicates.length > 1 ? "s" : ""}
              </Badge>
            )}
          </div>
        )}

        {step === 0 && <ImportDropzone onFile={analyzeFile} loading={analyzing} />}
        {step === 1 && data && (
          <ImportMapping
            headers={data.headers}
            mapping={data.mapping}
            sample={sampleRow}
            aiUsed={data.ai_used}
            onChange={setMapping}
          />
        )}
        {step === 2 && data && (
          <ImportDuplicates
            duplicates={data.duplicates || []}
            decisions={decisions}
            onChange={setDecisions}
            mapping={data.mapping}
            rows={data.rows}
          />
        )}
        {step === 3 && data && (
          <ImportPreview
            rows={data.rows}
            mapping={data.mapping}
            duplicates={data.duplicates || []}
            totalRows={data.total_rows}
          />
        )}
        {step === 4 && result && <ImportSuccess result={result} onReset={reset} />}
      </div>

      {/* Footer actions (hidden on success step) */}
      {step < 4 && (
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => (step === 0 ? null : setStep(step - 1))}
            disabled={step === 0}
            className={cn(
              "h-10 gap-1.5 text-muted-foreground hover:text-foreground",
              step === 0 && "opacity-0 pointer-events-none"
            )}
            data-testid="import-back-btn"
          >
            <ChevronLeft className="h-4 w-4" /> Retour
          </Button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                variant="ghost"
                onClick={reset}
                className="h-10 text-muted-foreground hover:text-foreground"
                data-testid="import-cancel-btn"
              >
                {tfr("Annuler")}
              </Button>
            )}
            {step < 3 && (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canNext}
                className="h-11 px-5 bg-primary text-primary-foreground hover:bg-primary/90 gap-2 shadow-sm"
                data-testid="import-next-btn"
              >
                Continuer <ChevronRight className="h-4 w-4" />
              </Button>
            )}
            {step === 3 && (
              <Button
                onClick={commit}
                disabled={committing || !requiredOk}
                className="h-11 px-5 bg-primary text-primary-foreground hover:bg-primary/90 gap-2 shadow-sm"
                data-testid="import-commit-btn"
              >
                {committing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Import en cours…
                  </>
                ) : (
                  <>Importer {data?.total_rows || 0} joueur{(data?.total_rows || 0) > 1 ? "s" : ""}</>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
